import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import {
  COUNT_OPERATORS,
  type CountOperator,
  type CountQuery as CountQueryType,
  type RangeFilterValue,
} from '@frontend/api';
import {
  IconButton,
  CloseIcon,
  Select,
  type SelectProps,
  SelectComponents,
  HStack,
  Text,
  EqualIcon,
  NotEqualIcon,
  GreaterThanIcon,
  GreaterThanOrEqualToIcon,
  LessThanIcon,
  LessThanOrEqualToIcon,
  RangeIcon,
} from '@frontend/components';

import { QueryWrapper } from './FilterQueryWrapped';
import { NumberValueField } from './NumberValueField';
import { type FormStateData } from './RulesDetailModal';

type CountQueryOptionLabelProps = {
  icon?: JSX.Element;
  text: string;
};

const CountQueryOptionLabel = ({ icon, text }: CountQueryOptionLabelProps) => (
  <HStack spacing={0.5} alignItems="center">
    {icon && icon}
    <Text>{text}</Text>
  </HStack>
);

type CountOperatorOption = {
  label: JSX.Element;
  value: CountOperator;
};

type GroupedCountOperatorOption = {
  label: string;
  options: CountOperatorOption[];
};

export const INITIAL_COUNT_QUERY_VALUE: CountQueryType = {
  operator: COUNT_OPERATORS.GREATER_THAN_OR_EQUAL,
  value: 1,
};

const EQUALITY_OPTIONS: CountOperatorOption[] = [
  {
    label: <CountQueryOptionLabel icon={<EqualIcon />} text="Count equal" />,
    value: COUNT_OPERATORS.EQUALS,
  },
  {
    label: (
      <CountQueryOptionLabel icon={<NotEqualIcon />} text="Count not equal" />
    ),
    value: COUNT_OPERATORS.NOT_EQUALS,
  },
];

const GREATER_THAN_OPTIONS: CountOperatorOption[] = [
  {
    label: (
      <CountQueryOptionLabel
        icon={<GreaterThanIcon />}
        text="Count greater than"
      />
    ),
    value: COUNT_OPERATORS.GREATER_THAN,
  },
  {
    label: (
      <CountQueryOptionLabel
        icon={<GreaterThanOrEqualToIcon />}
        text="Count greater than or equal"
      />
    ),
    value: COUNT_OPERATORS.GREATER_THAN_OR_EQUAL,
  },
];

const LESS_THAN_OPTIONS: CountOperatorOption[] = [
  {
    label: (
      <CountQueryOptionLabel icon={<LessThanIcon />} text="Count less than" />
    ),
    value: COUNT_OPERATORS.LESS_THAN,
  },
  {
    label: (
      <CountQueryOptionLabel
        icon={<LessThanOrEqualToIcon />}
        text="Count less than or equal"
      />
    ),
    value: COUNT_OPERATORS.LESS_THAN_OR_EQUAL,
  },
];

const RANGE_OPTIONS: CountOperatorOption[] = [
  {
    label: <CountQueryOptionLabel icon={<RangeIcon />} text="Count between" />,
    value: COUNT_OPERATORS.RANGE,
  },
];
const OPTIONS: GroupedCountOperatorOption[] = [
  { label: 'Count of occurrences', options: EQUALITY_OPTIONS },
  { label: '', options: GREATER_THAN_OPTIONS },
  { label: '', options: LESS_THAN_OPTIONS },
  { label: '', options: RANGE_OPTIONS },
];

const RANGE_OPERATORS = [COUNT_OPERATORS.RANGE];

const OperatorSelectContainer = (props: any) => (
  <SelectComponents.SelectContainer
    {...props}
    innerProps={{
      ...props.innerProps,
      flexGrow: 1,
      width: 'unset',
    }}
  />
);

const CountOperatorField = ({
  operator,
  onOperatorChange,
  rangeOperators = [],
  options,
  ...rest
}: SelectProps<CountOperatorOption> & {
  operator: CountOperator;
  rangeOperators?: CountOperator[];
  isDisabled?: boolean;
  onOperatorChange: (
    operator: CountOperator,
    value: number | RangeFilterValue | null,
  ) => void;
  options: GroupedCountOperatorOption[];
}) => {
  const handleOperatorChange = useCallback(
    (option: CountOperatorOption) => {
      // If we're going from a non-range to a range, initialize with 0
      if (
        !rangeOperators.includes(operator) &&
        rangeOperators.includes(option.value)
      ) {
        onOperatorChange(option.value, { min: 0, max: 0 });
        return;
      }
      // If we're going from a range to a non-range, reset to 0
      if (
        rangeOperators.includes(operator) &&
        !rangeOperators.includes(option.value)
      ) {
        onOperatorChange(option.value, 0);
        return;
      }
      // Otherwise, preserve the current value
      onOperatorChange(option.value, null);
    },
    [onOperatorChange, rangeOperators, operator],
  );

  const selectedOption = useMemo(() => {
    const flatOptions = options.flatMap((option) => option.options);
    return flatOptions.find((option) => option.value === operator);
  }, [options, operator]);

  return (
    <Select
      {...rest}
      options={options}
      value={selectedOption}
      onChange={handleOperatorChange}
      isClearable={false}
      components={{
        SelectContainer: OperatorSelectContainer,
      }}
    />
  );
};

export const CountQuery = (props: {
  operator: CountOperator;
  value: number | RangeFilterValue;
  isDisabled?: boolean;
  onChange: (operator: CountOperator, value: number | RangeFilterValue) => void;
  signalIndex: number;
  groupIndex: number;
  onClearCountQuery: () => void;
  hideRemoveButton?: boolean;
}) => {
  const {
    operator,
    value,
    isDisabled,
    onChange,
    signalIndex,
    groupIndex,
    onClearCountQuery,
    hideRemoveButton,
  } = props;
  const { getFieldProps } = useFormikContext<FormStateData>();

  const handleNumberChange = (value: number | RangeFilterValue) => {
    onChange(operator, value);
  };

  const handleOperatorChange = (
    operator: CountOperator,
    newValue: number | RangeFilterValue | null,
  ) => {
    if (newValue === null) {
      onChange(operator, value);
    } else {
      onChange(operator, newValue);
    }
  };

  return (
    <QueryWrapper className="count-query-wrapper">
      <CountOperatorField
        {...getFieldProps(
          `signals.${signalIndex}.groups.${groupIndex}.countQuery.operator`,
        )}
        options={OPTIONS}
        rangeOperators={RANGE_OPERATORS}
        isDisabled={isDisabled}
        operator={operator}
        onOperatorChange={handleOperatorChange}
        aria-label="Count modifier"
        flexBasis={12}
        isClearable={false}
        className="count-query-operator"
      />
      <NumberValueField
        {...getFieldProps(
          `signals.${signalIndex}.groups.${groupIndex}.countQuery.value`,
        )}
        isDisabled={isDisabled}
        value={value}
        onChange={handleNumberChange}
        min={0}
        step={1}
        precision={0}
        flexBasis={16}
        allowDecimals={false}
        className="count-query-value"
        isCountQuery={true}
      />
      {!hideRemoveButton && (
        <IconButton
          size="sm"
          m={0}
          mt={0.5}
          onClick={onClearCountQuery}
          aria-label="Remove count query"
          className="count-query-clear-button"
        >
          <CloseIcon />
        </IconButton>
      )}
    </QueryWrapper>
  );
};
